// UnifiedTransactionForm.jsx - Complete Unified Transaction API
// With Owner Account Category, Advanced Filters, Cheque Reversal Support, and Fabric Sale/Return Integration
// ADDED: Edit, Delete, and Reverse Transaction Features

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave, faUser, faBuilding, faTruck, faUsers,
    faWallet, faExchangeAlt, faCalendarAlt, faBarcode, faBolt, faBoxes, faPen, faTools, faShieldAlt, faLaptopCode, faPlane, faGraduationCap, faEllipsisH,
    faReceipt, faSave, faTimes, faSpinner, faCheckCircle,
    faExclamationTriangle, faCreditCard, faUniversity,
    faTag, faInfoCircle, faArrowRight, faArrowLeft,
    faShoppingCart, faHandHoldingUsd, faPercent, faFileInvoice,
    faUserPlus, faUserMinus, faBan, faClock, faHistory, faArrowDown, faArrowUp,
    faSearch, faChevronDown, faChevronUp, faDollarSign,
    faEuroSign, faPoundSign, faRupeeSign, faTrash, faEdit,
    faPlus, faMinus, faPrint, faDownload, faFilter,
    faChartLine, faBalanceScale, faBriefcase, faHome,
    faStore, faHandHoldingHeart, faHeart, faHandshake, faChevronLeft, faChevronRight,
    faSort, faSortUp, faSortDown, faSyncAlt, faEye,
    faChartPie, faArrowTrendUp, faArrowTrendDown, faBank, faPlusCircle
} from '@fortawesome/free-solid-svg-icons';
import "../styles/Accounting.css";

// ==============================================
// TOAST NOTIFICATION
// ==============================================
const UTToastNotification = ({ toast, setToast }) => {
    if (!toast.show) return null;
    return (
        <div className={`uni-toast-notification ${toast.type}`}>
            <div className="uni-toast-content">
                <div className="uni-toast-icon">
                    <FontAwesomeIcon icon={toast.type === 'success' ? faCheckCircle : faExclamationTriangle} />
                </div>
                <div className="uni-toast-text">
                    <div className="uni-toast-message">{toast.message}</div>
                </div>
                <button className="uni-toast-close" onClick={() => setToast({ show: false })}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// CURRENCY SELECTOR WITH MANUAL EXCHANGE RATE
// ==============================================
const UTCurrencySelector = ({ selectedCurrency, onCurrencyChange, exchangeRate, onRateChange, shopId, showRateInput = true }) => {
    const [loading, setLoading] = useState(false);
    const [showCurrencyModal, setShowCurrencyModal] = useState(false);
    const [showRateModal, setShowRateModal] = useState(false);
    const [tempRate, setTempRate] = useState(exchangeRate);
    const [manualRateInput, setManualRateInput] = useState('');
    const { user } = useAuth();

    const currencies = [
        { code: 'USD', symbol: '$', name: 'US Dollar', icon: faDollarSign, color: '#2ecc71' },
        { code: 'USDT', symbol: '₮', name: 'Tether USD', icon: faWallet, color: '#3498db' },
        { code: 'PKR', symbol: '₨', name: 'Pakistani Rupee', icon: faRupeeSign, color: '#e74c3c' },
        { code: 'INR', symbol: '', name: 'Indian Rupee', icon: faRupeeSign, color: '#f39c12' },
        { code: 'EUR', symbol: '€', name: 'Euro', icon: faEuroSign, color: '#1abc9c' },
        { code: 'GBP', symbol: '£', name: 'British Pound', icon: faPoundSign, color: '#9b59b6' },
        { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', icon: faMoneyBillWave, color: '#34495e' },
        { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal', icon: faMoneyBillWave, color: '#16a085' }
    ];

    const currentCurrency = currencies.find(c => c.code === selectedCurrency) || currencies[0];

    useEffect(() => {
        const loadExchangeRate = async () => {
            if (selectedCurrency === 'USDT') {
                setManualRateInput('1.000000');
                onRateChange(1);
                return;
            }
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/unified_transactions.php', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'get_exchange_rate', from_currency: selectedCurrency, to_currency: 'USDT', shop_id: shopId })
                });
                const data = await response.json();
                if (data.status === 'success' && data.data.rate) {
                    const loadedRate = data.data.rate;
                    setManualRateInput(loadedRate.toString());
                    onRateChange(loadedRate);
                } else {
                    setManualRateInput('');
                    onRateChange(0);
                }
            } catch (error) { 
                console.error('Error loading rate:', error);
                setManualRateInput('');
                onRateChange(0);
            }
        };
        if (selectedCurrency !== 'USDT') {
            loadExchangeRate();
        } else {
            setManualRateInput('1.000000');
            onRateChange(1);
        }
    }, [selectedCurrency, shopId]);

    const handleManualRateChange = (e) => {
        const value = e.target.value;
        setManualRateInput(value);
        const rate = parseFloat(value);
        if (!isNaN(rate) && rate > 0) {
            onRateChange(rate);
        } else {
            onRateChange(0);
        }
    };

    const handleRateSave = async () => {
        const rate = parseFloat(tempRate);
        if (!rate || rate <= 0) return;
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/unified_transactions.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'update_exchange_rate', from_currency: selectedCurrency, to_currency: 'USDT', rate: rate, shop_id: shopId, username: user?.username })
            });
            const data = await response.json();
            if (data.status === 'success') { 
                onRateChange(rate);
                setManualRateInput(rate.toString());
                setShowRateModal(false); 
            } else {
                alert('Failed to save rate: ' + (data.message || 'Unknown error'));
            }
        } catch (error) { console.error('Error saving rate:', error); alert('Network error while saving rate'); }
        finally { setLoading(false); }
    };

    const openRateModal = () => {
        setTempRate(exchangeRate || 1);
        setShowRateModal(true);
    };

    return (
        <>
            <div className="uni-currency-selector-compact">
                <button type="button" className="uni-currency-display-btn" onClick={() => setShowCurrencyModal(true)}>
                    <div className="uni-currency-display-content">
                        <FontAwesomeIcon icon={currentCurrency.icon} style={{ color: currentCurrency.color }} />
                        <div className="uni-currency-display-info">
                            <span className="uni-currency-display-code">{selectedCurrency}</span>
                            <span className="uni-currency-display-name">{currentCurrency.name}</span>
                        </div>
                        <FontAwesomeIcon icon={faChevronDown} className="uni-dropdown-arrow" />
                    </div>
                </button>
                
                {selectedCurrency !== 'USDT' && showRateInput && (
                    <div className="uni-manual-rate-input-wrapper">
                        <div className="uni-rate-label">
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>Exchange Rate (1 {selectedCurrency} = ? USDT)</span>
                        </div>
                        <div className="uni-rate-input-group">
                            <input
                                type="number"
                                step="0.000001"
                                value={manualRateInput}
                                onChange={handleManualRateChange}
                                placeholder="Enter rate manually"
                                className="uni-manual-rate-input"
                            />
                            <button type="button" className="uni-save-rate-btn" onClick={openRateModal}>
                                Save Rate
                            </button>
                        </div>
                        <small className="uni-rate-hint">Enter any value - not linked to other currencies automatically</small>
                    </div>
                )}
            </div>

            {showCurrencyModal && (
                <div className="uni-modal-overlay" onClick={() => setShowCurrencyModal(false)}>
                    <div className="uni-modal-content uni-currency-modal" onClick={e => e.stopPropagation()}>
                        <div className="uni-modal-header"><h3><FontAwesomeIcon icon={faMoneyBillWave} /> Select Currency</h3><button className="uni-modal-close" onClick={() => setShowCurrencyModal(false)}>×</button></div>
                        <div className="uni-modal-body">
                            <div className="uni-currency-modal-grid">
                                {currencies.map(currency => (
                                    <button key={currency.code} type="button" className={`uni-currency-option-btn ${selectedCurrency === currency.code ? 'active' : ''}`} onClick={() => { onCurrencyChange(currency.code); setShowCurrencyModal(false); }} style={{ borderColor: selectedCurrency === currency.code ? currency.color : '#e0e0e0', backgroundColor: selectedCurrency === currency.code ? `${currency.color}15` : 'white' }}>
                                        <div className="uni-currency-option-icon" style={{ backgroundColor: `${currency.color}20`, color: currency.color }}><FontAwesomeIcon icon={currency.icon} /></div>
                                        <div className="uni-currency-option-info"><div className="uni-currency-option-code">{currency.code}</div><div className="uni-currency-option-name">{currency.name}</div></div>
                                        <div className="uni-currency-option-symbol">{currency.symbol}</div>
                                        {selectedCurrency === currency.code && <div className="uni-currency-checkmark"><FontAwesomeIcon icon={faCheckCircle} style={{ color: currency.color }} /></div>}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="uni-modal-footer"><button className="uni-btn-secondary" onClick={() => setShowCurrencyModal(false)}>Close</button></div>
                    </div>
                </div>
            )}

            {showRateModal && (
                <div className="uni-modal-overlay" onClick={() => setShowRateModal(false)}>
                    <div className="uni-modal-content uni-rate-modal" onClick={e => e.stopPropagation()}>
                        <div className="uni-modal-header"><h3><FontAwesomeIcon icon={faExchangeAlt} /> Save Exchange Rate</h3><button className="uni-modal-close" onClick={() => setShowRateModal(false)}>×</button></div>
                        <div className="uni-modal-body">
                            <div className="uni-rate-conversion-preview">
                                <div className="uni-rate-from"><div className="uni-rate-currency-code">1 {selectedCurrency}</div><div className="uni-rate-currency-name">{currentCurrency.name}</div></div>
                                <div className="uni-rate-equals"><FontAwesomeIcon icon={faArrowRight} className="uni-rate-arrow" /></div>
                                <div className="uni-rate-to"><input type="number" step="0.000001" value={tempRate || ''} onChange={(e) => setTempRate(parseFloat(e.target.value) || 0)} className="uni-rate-input-large" placeholder="0.000000" /><div className="uni-rate-currency-code">USDT</div><div className="uni-rate-currency-name">Tether USD</div></div>
                            </div>
                            <div className="uni-rate-info-box"><FontAwesomeIcon icon={faInfoCircle} /><div className="uni-rate-info-text"><strong>Save Rate for Future Reference</strong><p>Saving this rate will store it in the database for later use, but the current transaction uses the manually entered rate above.</p></div></div>
                            <div className="uni-rate-examples"><div className="uni-example-item"><span>100 {selectedCurrency} =</span><strong>{(100 * (tempRate || 0)).toFixed(6)} USDT</strong></div><div className="uni-example-item"><span>500 {selectedCurrency} =</span><strong>{(500 * (tempRate || 0)).toFixed(6)} USDT</strong></div><div className="uni-example-item"><span>1,000 {selectedCurrency} =</span><strong>{(1000 * (tempRate || 0)).toFixed(6)} USDT</strong></div></div>
                        </div>
                        <div className="uni-modal-footer"><button className="uni-btn-secondary" onClick={() => setShowRateModal(false)}>Cancel</button><button className="uni-btn-primary" onClick={handleRateSave} disabled={loading || !tempRate}>Save Rate to Database</button></div>
                    </div>
                </div>
            )}
        </>
    );
};

// ==============================================
// ENTITY SEARCH DROPDOWN
// ==============================================
const UTEntitySearchDropdown = ({ entityType, onSelect, selectedEntity, shopId, placeholder = "Search...", required = false, excludeId = null }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [entities, setEntities] = useState([]);
    const [filteredEntities, setFilteredEntities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const searchRef = useRef(null);

    const loadEntities = useCallback(async () => {
        if (!shopId) return;
        setLoading(true);
        try {
            let endpoint = 'https://ourzestapp.online/oz_backend/unified_transactions.php';
            let payload = {};
            switch (entityType) {
                case 'customer': payload = { action: 'get_customers', shop_id: shopId }; break;
                case 'supplier': endpoint = 'https://ourzestapp.online/oz_backend/supplier.php'; payload = { action: 'get_all_suppliers_with_balance', shop_id: shopId }; break;
                case 'employee': payload = { action: 'get_employees', shop_id: shopId }; break;
                case 'partner': payload = { action: 'get_partners', shop_id: shopId }; break;
                case 'other': endpoint = 'https://ourzestapp.online/oz_backend/others_account.php'; payload = { action: 'get_all_others', shop_id: shopId }; break;
                default: return;
            }
            const response = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const data = await response.json();
            if (data.status === 'success') {
                let items = data.data || [];
                items = items.map(item => ({
                    id: item.partner_id || item.customer_id || item.id || item.supplierid || item.employeeid || item.other_id,
                    name: item.partner_name || item.customer_name || item.name || item.supplier_name || item.employee_name || item.other_name,
                    balance: item.current_balance || item.balance_due || 0,
                    phone: item.phone || null,
                    ...item
                }));
                if (excludeId) {
                    items = items.filter(item => item.id !== excludeId);
                }
                setEntities(items);
                setFilteredEntities(items);
            }
        } catch (error) { console.error('Error loading entities:', error); } finally { setLoading(false); }
    }, [shopId, entityType, excludeId]);

    useEffect(() => { if (shopId) loadEntities(); }, [shopId, entityType, excludeId, loadEntities]);
    useEffect(() => { const handleClickOutside = (event) => { if (searchRef.current && !searchRef.current.contains(event.target)) setShowDropdown(false); }; document.addEventListener('mousedown', handleClickOutside); return () => document.removeEventListener('mousedown', handleClickOutside); }, []);

    const handleSearch = (value) => {
        setSearchTerm(value);
        if (value.length >= 1) {
            const filtered = entities.filter(entity => entity.name?.toLowerCase().includes(value.toLowerCase()) || entity.phone?.includes(value));
            setFilteredEntities(filtered);
            setShowDropdown(true);
        } else {
            setFilteredEntities(entities);
            setShowDropdown(true);
        }
    };

    const handleSelect = (entity) => {
        setSearchTerm(entity.name);
        setShowDropdown(false);
        onSelect(entity);
    };

    const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    const getEntityLabel = () => ({ customer: 'Customer', supplier: 'Supplier', employee: 'Employee', partner: 'Partner', other: 'Other Account' }[entityType] || 'Entity');

    return (
        <div className="uni-entity-search-dropdown" ref={searchRef}>
            <div className="uni-search-input-wrapper">
                <FontAwesomeIcon icon={faSearch} className="uni-search-icon" />
                <input type="text" value={searchTerm} onChange={(e) => handleSearch(e.target.value)} onFocus={() => setShowDropdown(true)} placeholder={placeholder} className="uni-entity-search-input" autoComplete="off" />
                {loading && <FontAwesomeIcon icon={faSpinner} spin className="uni-loading-spinner" />}
                {required && !selectedEntity && <span className="uni-required-badge">Required</span>}
            </div>
            {showDropdown && (
                <div className="uni-entity-dropdown">
                    {filteredEntities.length > 0 ? filteredEntities.map(entity => (
                        <div key={entity.id} className="uni-entity-option" onClick={() => handleSelect(entity)}>
                            <div className="uni-entity-info">
                                <div className="uni-entity-name"><FontAwesomeIcon icon={faUser} />{entity.name}</div>
                                {entity.phone && <div className="uni-entity-phone">{entity.phone}</div>}
                            </div>
                            <div className={`uni-entity-balance ${parseFloat(entity.balance) > 0 ? 'positive' : ''}`}>Balance: {formatCurrency(entity.balance)}</div>
                        </div>
                    )) : <div className="uni-entity-dropdown-empty"><FontAwesomeIcon icon={faExclamationTriangle} /><span>No {getEntityLabel().toLowerCase()}s found</span></div>}
                </div>
            )}
            {selectedEntity && (
                <div className="uni-selected-entity-info">
                    <div className="uni-selected-entity-name"><FontAwesomeIcon icon={faCheckCircle} />{selectedEntity.name}</div>
                    {selectedEntity.phone && <div className="uni-selected-entity-phone">{selectedEntity.phone}</div>}
                    <div className={`uni-selected-entity-balance ${parseFloat(selectedEntity.balance) > 0 ? 'positive' : ''}`}>Balance: {formatCurrency(selectedEntity.balance)}</div>
                </div>
            )}
        </div>
    );
};

// ==============================================
// EDIT TRANSACTION MODAL
// ==============================================
const EditTransactionModal = ({ transaction, onClose, onSuccess, shopId }) => {
    const { user } = useAuth();
    const [formData, setFormData] = useState({
        amount: '',
        transaction_date: '',
        description: '',
        notes: '',
        reference_number: '',
        payment_method: ''
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (transaction) {
            setFormData({
                amount: transaction.amount,
                transaction_date: transaction.transaction_date?.split('T')[0] || '',
                description: transaction.description || '',
                notes: transaction.notes || '',
                reference_number: transaction.reference_number || '',
                payment_method: transaction.payment_method || 'cash'
            });
        }
    }, [transaction]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const newErrors = {};
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            newErrors.amount = 'Amount must be greater than 0';
        }
        if (!formData.transaction_date) {
            newErrors.transaction_date = 'Date is required';
        }
        
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/unified_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'update_transaction',
                    transaction_id: transaction.id,
                    amount: parseFloat(formData.amount),
                    transaction_date: formData.transaction_date,
                    description: formData.description,
                    notes: formData.notes,
                    reference_number: formData.reference_number,
                    payment_method: formData.payment_method,
                    shop_id: shopId,
                    username: user?.username
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                if (onSuccess) onSuccess();
                onClose();
            } else {
                setErrors({ submit: data.message || 'Update failed' });
            }
        } catch (error) {
            console.error('Error updating transaction:', error);
            setErrors({ submit: 'Network error occurred' });
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount || 0);

    const paymentMethodsList = [
        { value: 'cash', label: 'Cash', icon: faMoneyBillWave },
        { value: 'bank_transfer', label: 'Bank Transfer', icon: faUniversity },
        { value: 'cheque', label: 'Cheque', icon: faFileInvoice }
    ];

    if (!transaction) return null;

    const isIncoming = ['customer_payment', 'customer_refund', 'supplier_return', 'other_payment', 'partner_to_owner', 'other_to_owner'].includes(transaction.transaction_type);

    return (
        <div className="uni-modal-overlay" onClick={onClose}>
            <div className="uni-modal-content edit-modal" style={{ maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
                <div className="uni-modal-header">
                    <h3>
                        <FontAwesomeIcon icon={faEdit} /> 
                        Edit Transaction
                        <span className="transaction-badge" style={{ backgroundColor: isIncoming ? '#10b98120' : '#ef444420', color: isIncoming ? '#059669' : '#dc2626' }}>
                            {isIncoming ? 'INCOMING' : 'OUTGOING'}
                        </span>
                    </h3>
                    <button className="uni-modal-close" onClick={onClose}>×</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="uni-modal-body">
                        <div className="edit-info-row">
                            <div className="info-item">
                                <label>Transaction #</label>
                                <span>{transaction.transaction_no}</span>
                            </div>
                            <div className="info-item">
                                <label>Type</label>
                                <span>{transaction.transaction_type?.replace(/_/g, ' ')}</span>
                            </div>
                            {transaction.entity_name && (
                                <div className="info-item">
                                    <label>Entity</label>
                                    <span>{transaction.entity_type}: {transaction.entity_name}</span>
                                </div>
                            )}
                        </div>

                        <div className="uni-form-group">
                            <label>Amount *</label>
                            <div className="amount-input-wrapper">
                                <span className="currency-prefix">{transaction.currency_symbol || '$'}</span>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.amount}
                                    onChange={(e) => handleChange('amount', e.target.value)}
                                    className={`uni-form-input ${errors.amount ? 'error' : ''}`}
                                />
                                <span className="currency-suffix">{transaction.currency_code}</span>
                            </div>
                            {errors.amount && <div className="uni-error-message">{errors.amount}</div>}
                        </div>

                        <div className="uni-form-group">
                            <label>Transaction Date *</label>
                            <input
                                type="date"
                                value={formData.transaction_date}
                                onChange={(e) => handleChange('transaction_date', e.target.value)}
                                className={`uni-form-input ${errors.transaction_date ? 'error' : ''}`}
                            />
                            {errors.transaction_date && <div className="uni-error-message">{errors.transaction_date}</div>}
                        </div>

                        <div className="uni-form-group">
                            <label>Payment Method</label>
                            <div className="payment-methods-edit">
                                {paymentMethodsList.map(m => (
                                    <button
                                        key={m.value}
                                        type="button"
                                        className={`payment-method-option ${formData.payment_method === m.value ? 'active' : ''}`}
                                        onClick={() => handleChange('payment_method', m.value)}
                                    >
                                        <FontAwesomeIcon icon={m.icon} /> {m.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="uni-form-group">
                            <label>Reference Number</label>
                            <input
                                type="text"
                                value={formData.reference_number}
                                onChange={(e) => handleChange('reference_number', e.target.value)}
                                className="uni-form-input"
                                placeholder="Invoice/Reference #"
                            />
                        </div>

                        <div className="uni-form-group">
                            <label>Description</label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                className="uni-form-textarea"
                                rows="2"
                                placeholder="Transaction description"
                            />
                        </div>

                        <div className="uni-form-group">
                            <label>Notes</label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => handleChange('notes', e.target.value)}
                                className="uni-form-textarea"
                                rows="2"
                                placeholder="Additional notes"
                            />
                        </div>

                        {errors.submit && (
                            <div className="uni-error-message submit-error">
                                <FontAwesomeIcon icon={faExclamationTriangle} /> {errors.submit}
                            </div>
                        )}

                        <div className="edit-warning">
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            <span>Note: If you change the amount, the original transaction will be reversed and a new one created. This will create a reversal record for audit purposes.</span>
                        </div>
                    </div>
                    <div className="uni-modal-footer">
                        <button type="button" className="uni-btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="uni-btn-primary" disabled={loading}>
                            {loading ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</> : <><FontAwesomeIcon icon={faSave} /> Save Changes</>}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ==============================================
// DELETE/REVERSE CONFIRMATION MODAL
// ==============================================
const DeleteTransactionModal = ({ transaction, onClose, onSuccess, shopId }) => {
    const { user } = useAuth();
    const [reason, setReason] = useState('');
    const [hardDelete, setHardDelete] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleDelete = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/unified_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete_transaction',
                    transaction_id: transaction.id,
                    shop_id: shopId,
                    username: user?.username,
                    reason: reason || 'No reason provided',
                    hard_delete: hardDelete
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                if (onSuccess) onSuccess();
                onClose();
            } else {
                setError(data.message || 'Delete failed');
            }
        } catch (error) {
            console.error('Error deleting transaction:', error);
            setError('Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount || 0);

    if (!transaction) return null;

    const isIncoming = ['customer_payment', 'customer_refund', 'supplier_return', 'other_payment', 'partner_to_owner', 'other_to_owner'].includes(transaction.transaction_type);

    return (
        <div className="uni-modal-overlay" onClick={onClose}>
            <div className="uni-modal-content delete-modal" style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
                <div className="uni-modal-header warning">
                    <h3>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {hardDelete ? 'Delete Transaction' : 'Reverse Transaction'}
                    </h3>
                    <button className="uni-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="uni-modal-body">
                    <div className="transaction-summary">
                        <div className="summary-row">
                            <span>Transaction #:</span>
                            <strong>{transaction.transaction_no}</strong>
                        </div>
                        <div className="summary-row">
                            <span>Date:</span>
                            <span>{formatDate(transaction.transaction_date)}</span>
                        </div>
                        <div className="summary-row">
                            <span>Type:</span>
                            <span className={`type-badge ${isIncoming ? 'incoming' : 'outgoing'}`}>
                                {transaction.transaction_type?.replace(/_/g, ' ')}
                            </span>
                        </div>
                        <div className="summary-row">
                            <span>Amount:</span>
                            <span className={`amount ${isIncoming ? 'positive' : 'negative'}`}>
                                {isIncoming ? '+' : '-'}{formatCurrency(transaction.amount)} {transaction.currency_code}
                            </span>
                        </div>
                        {transaction.entity_name && (
                            <div className="summary-row">
                                <span>Entity:</span>
                                <span>{transaction.entity_type}: {transaction.entity_name}</span>
                            </div>
                        )}
                    </div>

                    <div className="delete-options">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={hardDelete}
                                onChange={(e) => setHardDelete(e.target.checked)}
                            />
                            <span>Hard Delete (Permanently remove from database - irreversible)</span>
                        </label>
                        {!hardDelete && (
                            <div className="info-message">
                                <FontAwesomeIcon icon={faInfoCircle} />
                                <span>Soft delete will reverse the transaction and create a reversal record for audit purposes.</span>
                            </div>
                        )}
                        {hardDelete && (
                            <div className="warning-message">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                <span><strong>Warning:</strong> This action is irreversible! The transaction will be permanently removed from the database.</span>
                            </div>
                        )}
                    </div>

                    <div className="uni-form-group">
                        <label>Reason for {hardDelete ? 'deletion' : 'reversal'} *</label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="uni-form-textarea"
                            rows="2"
                            placeholder="Please provide a reason..."
                            required
                        />
                    </div>

                    {error && (
                        <div className="uni-error-message">
                            <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
                        </div>
                    )}
                </div>
                <div className="uni-modal-footer">
                    <button type="button" className="uni-btn-secondary" onClick={onClose}>
                        Cancel
                    </button>
                    <button 
                        type="button" 
                        className={`uni-btn-danger ${hardDelete ? 'hard-delete' : ''}`} 
                        onClick={handleDelete} 
                        disabled={loading || !reason.trim()}
                    >
                        {loading ? <><FontAwesomeIcon icon={faSpinner} spin /> Processing...</> : (hardDelete ? 'Delete Permanently' : 'Reverse Transaction')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// OWNER ACCOUNT TRANSACTIONS COMPONENT
// ==============================================
const OwnerTransactions = ({ shopId, user }) => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    // Multi-select states
    const [isMultiSelectOpen, setIsMultiSelectOpen] = useState(false);
    const [typeSearchTerm, setTypeSearchTerm] = useState('');
    
    const [summary, setSummary] = useState({
        total_incoming: 0,
        total_outgoing: 0,
        net_balance: 0,
        total_count: 0,
        today_incoming: 0,
        today_outgoing: 0,
        month_incoming: 0,
        month_outgoing: 0,
        current_balance: 0,
        monthly_breakdown: [],
        top_customers: [],
        expense_categories: []
    });
    const [filters, setFilters] = useState({
        transaction_types: [],
        direction: '',
        entity_type: '',
        from_date: '',
        to_date: '',
        min_amount: '',
        max_amount: '',
        payment_method: '',
        search_text: ''
    });
    const [activeSummaryTab, setActiveSummaryTab] = useState('overview');

    const transactionTypes = {
        incoming: [
            { value: 'customer_payment', label: 'Customer Payment', icon: faUser },
            { value: 'customer_refund', label: 'Customer Refund', icon: faUserMinus },
            { value: 'supplier_return', label: 'Supplier Return', icon: faTruck },
            { value: 'other_payment', label: 'Other Payment', icon: faBuilding },
            { value: 'partner_to_owner', label: 'Partner → Owner', icon: faHandshake },
            { value: 'other_to_owner', label: 'Other → Owner', icon: faArrowRight },
            { value: 'owner_commission', label: 'Commission', icon: faPercent },
            { value: 'owner_transfer_in', label: 'Transfer In', icon: faArrowDown }
        ],
        outgoing: [
            { value: 'supplier_payment', label: 'Supplier Payment', icon: faTruck },
            { value: 'salary_payment', label: 'Salary Payment', icon: faUsers },
            { value: 'salary_advance', label: 'Salary Advance', icon: faClock },
            { value: 'other_purchase', label: 'Other Purchase', icon: faShoppingCart },
            { value: 'general_expense', label: 'General Expense', icon: faReceipt },
            { value: 'owner_expense', label: 'Owner Expense', icon: faUser },
            { value: 'owner_transfer_out', label: 'Transfer Out', icon: faArrowUp },
            { value: 'owner_to_other', label: 'Owner → Other', icon: faArrowRight }
        ]
    };

    const allTransactionTypes = [
        ...transactionTypes.incoming,
        ...transactionTypes.outgoing
    ];

    const filteredTransactionTypes = allTransactionTypes.filter(tx =>
        tx.label.toLowerCase().includes(typeSearchTerm.toLowerCase())
    );

    const entityTypes = [
        { value: 'customer', label: 'Customer', icon: faUser },
        { value: 'supplier', label: 'Supplier', icon: faTruck },
        { value: 'employee', label: 'Employee', icon: faUsers },
        { value: 'other', label: 'Other', icon: faBuilding }
    ];

    const paymentMethods = [
        { value: 'cash', label: 'Cash', icon: faMoneyBillWave },
        { value: 'bank_transfer', label: 'Bank Transfer', icon: faUniversity },
        { value: 'cheque', label: 'Cheque', icon: faFileInvoice }
    ];

    const toggleTransactionType = (typeValue) => {
        setFilters(prev => {
            const currentTypes = [...prev.transaction_types];
            if (currentTypes.includes(typeValue)) {
                return { ...prev, transaction_types: currentTypes.filter(t => t !== typeValue) };
            } else {
                return { ...prev, transaction_types: [...currentTypes, typeValue] };
            }
        });
        setCurrentPage(1);
    };

    const clearSelectedTypes = () => {
        setFilters(prev => ({ ...prev, transaction_types: [] }));
        setCurrentPage(1);
    };

    // Close multi-select dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            if (isMultiSelectOpen) {
                setIsMultiSelectOpen(false);
                setTypeSearchTerm('');
            }
        };
        
        if (isMultiSelectOpen) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [isMultiSelectOpen]);

    useEffect(() => {
        if (shopId) {
            loadTransactions();
            loadSummary();
        }
    }, [shopId, filters, currentPage]);

    const loadTransactions = async () => {
        setLoading(true);
        try {
            const params = {
                action: 'get_owner_transactions',
                shop_id: shopId,
                limit: itemsPerPage,
                offset: (currentPage - 1) * itemsPerPage,
                transaction_type: filters.transaction_types.join(','),
                direction: filters.direction,
                entity_type: filters.entity_type,
                from_date: filters.from_date,
                to_date: filters.to_date,
                min_amount: filters.min_amount,
                max_amount: filters.max_amount,
                payment_method: filters.payment_method,
                search_text: filters.search_text
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/unified_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params)
            });
            const data = await response.json();

            if (data.status === 'success') {
                setTransactions(data.data.transactions || []);
                setTotalCount(data.data.total_count || 0);
                if (data.data.summary) {
                    setSummary(prev => ({ ...prev, ...data.data.summary }));
                }
            }
        } catch (error) {
            console.error('Error loading owner transactions:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadSummary = async () => {
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/unified_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_owner_summary', shop_id: shopId })
            });
            const data = await response.json();
            if (data.status === 'success' && data.data) {
                setSummary(prev => ({ ...prev, ...data.data }));
            }
        } catch (error) {
            console.error('Error loading summary:', error);
        }
    };

    const resetFilters = () => {
        setFilters({
            transaction_types: [],
            direction: '',
            entity_type: '',
            from_date: '',
            to_date: '',
            min_amount: '',
            max_amount: '',
            payment_method: '',
            search_text: ''
        });
        setCurrentPage(1);
    };

    const formatCurrency = (amount) => new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount || 0);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getTransactionIcon = (type) => {
        if (transactionTypes.incoming.find(t => t.value === type)) return faArrowDown;
        if (transactionTypes.outgoing.find(t => t.value === type)) return faArrowUp;
        return faExchangeAlt;
    };

    const getTransactionColor = (type) => {
        const isIncoming = transactionTypes.incoming.find(t => t.value === type);
        const isOutgoing = transactionTypes.outgoing.find(t => t.value === type);
        if (isIncoming) return '#10b981';
        if (isOutgoing) return '#ef4444';
        return '#3b82f6';
    };

    const getTransactionLabel = (type) => {
        const incoming = transactionTypes.incoming.find(t => t.value === type);
        if (incoming) return incoming.label;
        const outgoing = transactionTypes.outgoing.find(t => t.value === type);
        if (outgoing) return outgoing.label;
        return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const totalPages = Math.ceil(totalCount / itemsPerPage);

    return (
        <div className="owner-account-container">
            <div className="owner-summary-grid">
                <div className="summary-card balance">
                    <div className="summary-card-icon">
                        <FontAwesomeIcon icon={faWallet} />
                    </div>
                    <div className="summary-card-content">
                        <span className="summary-card-label">Current Balance</span>
                        <span className="summary-card-value">{formatCurrency(summary.current_balance || summary.net_balance)}</span>
                        <span className="summary-card-sub">USDT</span>
                    </div>
                </div>
                <div className="summary-card incoming">
                    <div className="summary-card-icon">
                        <FontAwesomeIcon icon={faArrowDown} />
                    </div>
                    <div className="summary-card-content">
                        <span className="summary-card-label">Total Incoming</span>
                        <span className="summary-card-value">+{formatCurrency(summary.total_incoming)}</span>
                        <span className="summary-card-sub">All time</span>
                    </div>
                </div>
                <div className="summary-card outgoing">
                    <div className="summary-card-icon">
                        <FontAwesomeIcon icon={faArrowUp} />
                    </div>
                    <div className="summary-card-content">
                        <span className="summary-card-label">Total Outgoing</span>
                        <span className="summary-card-value">-{formatCurrency(summary.total_outgoing)}</span>
                        <span className="summary-card-sub">All time</span>
                    </div>
                </div>
                <div className="summary-card month">
                    <div className="summary-card-icon">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                    </div>
                    <div className="summary-card-content">
                        <span className="summary-card-label">This Month</span>
                        <span className="summary-card-value">+{formatCurrency(summary.month_incoming)}</span>
                        <span className="summary-card-value outgoing">-{formatCurrency(summary.month_outgoing)}</span>
                        <span className="summary-card-sub">Incoming / Outgoing</span>
                    </div>
                </div>
            </div>

            <div className="owner-summary-tabs">
                <button className={`summary-tab ${activeSummaryTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveSummaryTab('overview')}>
                    <FontAwesomeIcon icon={faChartLine} /> Overview
                </button>
                <button className={`summary-tab ${activeSummaryTab === 'monthly' ? 'active' : ''}`} onClick={() => setActiveSummaryTab('monthly')}>
                    <FontAwesomeIcon icon={faCalendarAlt} /> Monthly Breakdown
                </button>
                <button className={`summary-tab ${activeSummaryTab === 'customers' ? 'active' : ''}`} onClick={() => setActiveSummaryTab('customers')}>
                    <FontAwesomeIcon icon={faUser} /> Top Customers
                </button>
                <button className={`summary-tab ${activeSummaryTab === 'expenses' ? 'active' : ''}`} onClick={() => setActiveSummaryTab('expenses')}>
                    <FontAwesomeIcon icon={faReceipt} /> Expense Categories
                </button>
            </div>

            <div className="owner-summary-content">
                {activeSummaryTab === 'overview' && (
                    <div className="overview-stats">
                        <div className="stat-row">
                            <div className="stat-item">
                                <span className="stat-label">Today's Income</span>
                                <span className="stat-value positive">+{formatCurrency(summary.today_incoming)}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Today's Expenses</span>
                                <span className="stat-value negative">-{formatCurrency(summary.today_outgoing)}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Total Transactions</span>
                                <span className="stat-value">{totalCount}</span>
                            </div>
                        </div>
                    </div>
                )}

                {activeSummaryTab === 'monthly' && (
                    <div className="monthly-breakdown">
                        <table className="monthly-table">
                            <thead>
                                <tr><th>Month</th><th>Incoming</th><th>Outgoing</th><th>Net</th></tr>
                            </thead>
                            <tbody>
                                {summary.monthly_breakdown?.map(month => (
                                    <tr key={month.month}>
                                        <td>{month.month}</td>
                                        <td className="positive">+{formatCurrency(month.incoming)}</td>
                                        <td className="negative">-{formatCurrency(month.outgoing)}</td>
                                        <td className={month.incoming - month.outgoing >= 0 ? 'positive' : 'negative'}>
                                            {formatCurrency(month.incoming - month.outgoing)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeSummaryTab === 'customers' && (
                    <div className="top-customers">
                        <table className="customers-table">
                            <thead>
                                <tr><th>Customer</th><th>Total Paid</th><th>Transactions</th></tr>
                            </thead>
                            <tbody>
                                {summary.top_customers?.map(customer => (
                                    <tr key={customer.entity_id}>
                                        <td>{customer.entity_name}</td>
                                        <td className="positive">{formatCurrency(customer.total_paid)}</td>
                                        <td>{customer.transaction_count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeSummaryTab === 'expenses' && (
                    <div className="expense-categories">
                        <table className="expenses-table">
                            <thead>
                                <tr><th>Category</th><th>Total Amount</th><th>Count</th></tr>
                            </thead>
                            <tbody>
                                {summary.expense_categories?.map(cat => (
                                    <tr key={cat.expense_category_name}>
                                        <td>{cat.expense_category_name}</td>
                                        <td className="negative">{formatCurrency(cat.total_amount)}</td>
                                        <td>{cat.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="owner-filter-bar">
                <div className="filter-row">
                    <button
                        className={`filter-toggle ${showFilters ? 'active' : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <FontAwesomeIcon icon={faFilter} /> Filters
                        {filters.transaction_types.length > 0 && <span className="filter-badge"></span>}
                    </button>

                    <div className="search-wrapper">
                        <FontAwesomeIcon icon={faSearch} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search transactions..."
                            value={filters.search_text}
                            onChange={(e) => setFilters(prev => ({ ...prev, search_text: e.target.value }))}
                            onKeyPress={(e) => e.key === 'Enter' && loadTransactions()}
                            className="search-input"
                        />
                    </div>

                    <div className="direction-tabs">
                        <button
                            className={`direction-tab ${filters.direction === '' ? 'active' : ''}`}
                            onClick={() => setFilters(prev => ({ ...prev, direction: '' }))}
                        >
                            All
                        </button>
                        <button
                            className={`direction-tab incoming ${filters.direction === 'incoming' ? 'active' : ''}`}
                            onClick={() => setFilters(prev => ({ ...prev, direction: 'incoming' }))}
                        >
                            <FontAwesomeIcon icon={faArrowDown} /> Incoming
                        </button>
                        <button
                            className={`direction-tab outgoing ${filters.direction === 'outgoing' ? 'active' : ''}`}
                            onClick={() => setFilters(prev => ({ ...prev, direction: 'outgoing' }))}
                        >
                            <FontAwesomeIcon icon={faArrowUp} /> Outgoing
                        </button>
                    </div>

                    <button className="refresh-btn" onClick={loadTransactions}>
                        <FontAwesomeIcon icon={faSyncAlt} spin={loading} />
                    </button>
                </div>

                {showFilters && (
                    <div className="advanced-filters">
                        <div className="filter-grid">
                            <div className="filter-group full-width">
                                <label>Transaction Types (Select Multiple)</label>
                                <div style={{ position: 'relative' }}>
                                    <div 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsMultiSelectOpen(!isMultiSelectOpen);
                                        }}
                                        style={{
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '8px',
                                            padding: '8px 12px',
                                            minHeight: '42px',
                                            cursor: 'pointer',
                                            background: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: '8px'
                                        }}
                                    >
                                        {filters.transaction_types.length === 0 ? (
                                            <span style={{ color: '#94a3b8' }}>Select transaction types...</span>
                                        ) : (
                                            <>
                                                {filters.transaction_types.map(type => {
                                                    const tx = allTransactionTypes.find(t => t.value === type);
                                                    return (
                                                        <span key={type} style={{
                                                            background: '#e2e8f0',
                                                            padding: '4px 8px',
                                                            borderRadius: '6px',
                                                            fontSize: '0.75rem',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '6px'
                                                        }}>
                                                            {tx?.label || type}
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleTransactionType(type);
                                                                }}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    color: '#ef4444',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '14px',
                                                                    padding: '0 2px'
                                                                }}
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    );
                                                })}
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        clearSelectedTypes();
                                                    }}
                                                    style={{
                                                        background: '#fee2e2',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        padding: '4px 8px',
                                                        fontSize: '0.7rem',
                                                        cursor: 'pointer',
                                                        color: '#dc2626'
                                                    }}
                                                >
                                                    Clear all
                                                </button>
                                            </>
                                        )}
                                        <span style={{ marginLeft: 'auto', color: '#94a3b8' }}>▼</span>
                                    </div>

                                    {isMultiSelectOpen && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            marginTop: '4px',
                                            background: 'white',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '8px',
                                            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)',
                                            zIndex: 1000,
                                            maxHeight: '300px',
                                            overflow: 'auto'
                                        }}>
                                            <div style={{ padding: '8px', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, background: 'white' }}>
                                                <input
                                                    type="text"
                                                    placeholder="Search transaction types..."
                                                    value={typeSearchTerm}
                                                    onChange={(e) => setTypeSearchTerm(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '6px',
                                                        outline: 'none',
                                                        boxSizing: 'border-box'
                                                    }}
                                                />
                                            </div>
                                            <div style={{ maxHeight: '240px', overflow: 'auto' }}>
                                                {filteredTransactionTypes.length === 0 ? (
                                                    <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>
                                                        No transaction types found
                                                    </div>
                                                ) : (
                                                    filteredTransactionTypes.map(tx => (
                                                        <label
                                                            key={tx.value}
                                                            onClick={(e) => e.stopPropagation()}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '10px',
                                                                padding: '10px 12px',
                                                                cursor: 'pointer',
                                                                borderBottom: '1px solid #f1f5f9'
                                                            }}
                                                            onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={filters.transaction_types.includes(tx.value)}
                                                                onChange={() => toggleTransactionType(tx.value)}
                                                                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                                            />
                                                            <span style={{
                                                                background: transactionTypes.incoming.find(t => t.value === tx.value) ? '#10b98120' : '#ef444420',
                                                                color: transactionTypes.incoming.find(t => t.value === tx.value) ? '#059669' : '#dc2626',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                fontSize: '0.6rem',
                                                                fontWeight: 'bold',
                                                                minWidth: '32px',
                                                                textAlign: 'center'
                                                            }}>
                                                                {transactionTypes.incoming.find(t => t.value === tx.value) ? 'IN' : 'OUT'}
                                                            </span>
                                                            <span style={{ flex: 1 }}>
                                                                <FontAwesomeIcon icon={tx.icon} style={{ marginRight: '8px', fontSize: '0.75rem', color: '#64748b' }} />
                                                                {tx.label}
                                                            </span>
                                                        </label>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="filter-group">
                                <label>Entity Type</label>
                                <select
                                    value={filters.entity_type}
                                    onChange={(e) => setFilters(prev => ({ ...prev, entity_type: e.target.value }))}
                                    className="filter-select"
                                >
                                    <option value="">All Entities</option>
                                    {entityTypes.map(e => (
                                        <option key={e.value} value={e.value}>{e.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-group">
                                <label>Payment Method</label>
                                <select
                                    value={filters.payment_method}
                                    onChange={(e) => setFilters(prev => ({ ...prev, payment_method: e.target.value }))}
                                    className="filter-select"
                                >
                                    <option value="">All Methods</option>
                                    {paymentMethods.map(m => (
                                        <option key={m.value} value={m.value}>{m.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-group">
                                <label>From Date</label>
                                <input
                                    type="date"
                                    value={filters.from_date}
                                    onChange={(e) => setFilters(prev => ({ ...prev, from_date: e.target.value }))}
                                    className="filter-input"
                                />
                            </div>

                            <div className="filter-group">
                                <label>To Date</label>
                                <input
                                    type="date"
                                    value={filters.to_date}
                                    onChange={(e) => setFilters(prev => ({ ...prev, to_date: e.target.value }))}
                                    className="filter-input"
                                />
                            </div>

                            <div className="filter-group">
                                <label>Min Amount</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={filters.min_amount}
                                    onChange={(e) => setFilters(prev => ({ ...prev, min_amount: e.target.value }))}
                                    className="filter-input"
                                />
                            </div>

                            <div className="filter-group">
                                <label>Max Amount</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    placeholder="999999"
                                    value={filters.max_amount}
                                    onChange={(e) => setFilters(prev => ({ ...prev, max_amount: e.target.value }))}
                                    className="filter-input"
                                />
                            </div>
                        </div>

                        <div className="filter-actions">
                            <button className="btn-clear" onClick={resetFilters}>
                                <FontAwesomeIcon icon={faTimes} /> Clear All
                            </button>
                            <button className="btn-apply" onClick={loadTransactions}>
                                <FontAwesomeIcon icon={faCheckCircle} /> Apply Filters
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="owner-transactions-table-container">
                {loading ? (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading transactions...</p>
                    </div>
                ) : transactions.length === 0 ? (
                    <div className="empty-state">
                        <FontAwesomeIcon icon={faHistory} />
                        <p>No owner transactions found</p>
                    </div>
                ) : (
                    <table className="owner-transactions-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Entity</th>
                                <th>Description</th>
                                <th>Amount</th>
                                <th>Payment Method</th>
                                <th>Reference</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.map(tx => (
                                <tr key={tx.id}>
                                    <td data-label="Date">{formatDate(tx.transaction_date)}</td>
                                    <td data-label="Type">
                                        <span
                                            className="type-badge"
                                            style={{
                                                backgroundColor: `${getTransactionColor(tx.transaction_type)}20`,
                                                color: getTransactionColor(tx.transaction_type)
                                            }}
                                        >
                                            <FontAwesomeIcon icon={getTransactionIcon(tx.transaction_type)} />
                                            {getTransactionLabel(tx.transaction_type)}
                                        </span>
                                    </td>
                                    <td data-label="Entity">
                                        {tx.entity_name ? (
                                            <div className="entity-info">
                                                <span className="entity-type-badge">{tx.entity_type}</span>
                                                <span>{tx.entity_name}</span>
                                            </div>
                                        ) : (
                                            tx.from_partner_name && <div>Partner: {tx.from_partner_name}</div>
                                        )}
                                    </td>
                                    <td data-label="Description" className="desc-cell">
                                        {tx.description || '-'}
                                    </td>
                                    <td
                                        data-label="Amount"
                                        className={`amount-cell ${
                                            transactionTypes.incoming.find(t => t.value === tx.transaction_type)
                                                ? 'incoming'
                                                : 'outgoing'
                                        }`}
                                    >
                                        {transactionTypes.incoming.find(t => t.value === tx.transaction_type) ? '+' : '-'}
                                        {formatCurrency(tx.amount)}
                                    </td>
                                    <td data-label="Payment Method">
                                        <span className="payment-method-badge">
                                            <FontAwesomeIcon
                                                icon={
                                                    paymentMethods.find(m => m.value === tx.payment_method)?.icon ||
                                                    faMoneyBillWave
                                                }
                                            />
                                            {tx.payment_method || 'cash'}
                                        </span>
                                    </td>
                                    <td data-label="Reference">
                                        {tx.reference_number || '-'}
                                    </td>
                                    <td data-label="Actions">
                                        <div className="action-buttons">
                                            <button
                                                className="edit-btn"
                                                onClick={() => {
                                                    setSelectedTransaction(tx);
                                                    setShowEditModal(true);
                                                }}
                                                title="Edit transaction"
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button
                                                className="delete-btn"
                                                onClick={() => {
                                                    setSelectedTransaction(tx);
                                                    setShowDeleteModal(true);
                                                }}
                                                title="Delete/Reverse transaction"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                            <button
                                                className="view-btn"
                                                onClick={() => {
                                                    setSelectedTransaction(tx);
                                                    setShowDetailsModal(true);
                                                }}
                                                title="View details"
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {totalPages > 1 && (
                <div className="pagination-controls">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                        <FontAwesomeIcon icon={faChevronLeft} /> Previous
                    </button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                        Next <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            )}

            {showDetailsModal && selectedTransaction && (
                <div className="uni-modal-overlay" onClick={() => setShowDetailsModal(false)}>
                    <div className="uni-modal-content details-modal" onClick={e => e.stopPropagation()}>
                        <div className="uni-modal-header">
                            <h3><FontAwesomeIcon icon={faReceipt} /> Transaction Details</h3>
                            <button className="uni-modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
                        </div>
                        <div className="uni-modal-body">
                            <div className="details-grid">
                                <div className="detail-item"><label>Transaction #</label><span>{selectedTransaction.transaction_no}</span></div>
                                <div className="detail-item"><label>Date</label><span>{formatDate(selectedTransaction.transaction_date)}</span></div>
                                <div className="detail-item"><label>Type</label><span>{getTransactionLabel(selectedTransaction.transaction_type)}</span></div>
                                <div className="detail-item"><label>Amount</label><span className={transactionTypes.incoming.find(t => t.value === selectedTransaction.transaction_type) ? 'incoming' : 'outgoing'}>
                                    {transactionTypes.incoming.find(t => t.value === selectedTransaction.transaction_type) ? '+' : '-'}{formatCurrency(selectedTransaction.amount)} {selectedTransaction.currency_code}
                                </span></div>
                                {selectedTransaction.entity_name && <div className="detail-item"><label>Entity</label><span>{selectedTransaction.entity_type}: {selectedTransaction.entity_name}</span></div>}
                                {selectedTransaction.from_partner_name && <div className="detail-item"><label>From Partner</label><span>{selectedTransaction.from_partner_name}</span></div>}
                                {selectedTransaction.payment_method && <div className="detail-item"><label>Payment Method</label><span>{selectedTransaction.payment_method}</span></div>}
                                {selectedTransaction.reference_number && <div className="detail-item"><label>Reference</label><span>{selectedTransaction.reference_number}</span></div>}
                                {selectedTransaction.description && <div className="detail-item full-width"><label>Description</label><span>{selectedTransaction.description}</span></div>}
                                {selectedTransaction.notes && <div className="detail-item full-width"><label>Notes</label><span>{selectedTransaction.notes}</span></div>}
                            </div>
                        </div>
                        <div className="uni-modal-footer">
                            <button className="uni-btn-secondary" onClick={() => setShowDetailsModal(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {showEditModal && selectedTransaction && (
                <EditTransactionModal
                    transaction={selectedTransaction}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedTransaction(null);
                    }}
                    onSuccess={() => {
                        loadTransactions();
                        loadSummary();
                    }}
                    shopId={shopId}
                />
            )}

            {showDeleteModal && selectedTransaction && (
                <DeleteTransactionModal
                    transaction={selectedTransaction}
                    onClose={() => {
                        setShowDeleteModal(false);
                        setSelectedTransaction(null);
                    }}
                    onSuccess={() => {
                        loadTransactions();
                        loadSummary();
                    }}
                    shopId={shopId}
                />
            )}
        </div>
    );
};

// ==============================================
// MAIN UNIFIED TRANSACTION FORM
// ==============================================
const UnifiedTransactionForm = () => {
    const { user, currentShop } = useAuth();
    const [activeTab, setActiveTab] = useState('transactions');
    const [shopId, setShopId] = useState(() => currentShop?.shop_id || user?.shop_id || localStorage.getItem('current_shop_id') || '');

    const [transactionCategory, setTransactionCategory] = useState('customer');
    const [transactionSubType, setTransactionSubType] = useState('');

    const [selectedFromCustomer, setSelectedFromCustomer] = useState(null);
    const [selectedToCustomer, setSelectedToCustomer] = useState(null);
    const [selectedFromPartner, setSelectedFromPartner] = useState(null);
    const [selectedToPartner, setSelectedToPartner] = useState(null);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedOther, setSelectedOther] = useState(null);
    const [selectedSupplier, setSelectedSupplier] = useState(null);

    const [amount, setAmount] = useState('');
    const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);
    const [referenceNo, setReferenceNo] = useState('');
    const [notes, setNotes] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [expenseCategory, setExpenseCategory] = useState(null);
    const [selectedCurrency, setSelectedCurrency] = useState('USD');
    const [exchangeRate, setExchangeRate] = useState(0);
    const [convertedAmount, setConvertedAmount] = useState(0);

    const [paymentSource, setPaymentSource] = useState('owner');
    const [selectedPartner, setSelectedPartner] = useState(null);

    const [selectedCheque, setSelectedCheque] = useState(null);
    const [customerCheques, setCustomerCheques] = useState([]);
    const [loadingCheques, setLoadingCheques] = useState(false);
    const [chequeDetails, setChequeDetails] = useState({
        cheque_no: '',
        bank_name: '',
        cheque_date: '',
        amount: ''
    });

    const [showAddChequeModal, setShowAddChequeModal] = useState(false);
    const [savingCheque, setSavingCheque] = useState(false);
    const [newCheque, setNewCheque] = useState({
        cheque_no: '',
        bank_name: '',
        payee_name: '',
        amount: '',
        cheque_date: new Date().toISOString().split('T')[0],
        issue_date: new Date().toISOString().split('T')[0],
        currency_code: 'USD',
        currency_symbol: '$',
        remarks: ''
    });

    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [partnersList, setPartnersList] = useState([]);
    const [ownerBalance, setOwnerBalance] = useState(0);
    const [refreshHistory, setRefreshHistory] = useState(0);
    const [showSalaryModal, setShowSalaryModal] = useState(false);

    const currentLanguage = localStorage.getItem('ourzest_language') || 'en';
    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // ==============================================
    // FORMAT CURRENCY - MOVED HERE BEFORE ANY USAGE
    // ==============================================
    const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 6 
    }).format(amount || 0);

    const transactionCategories = [
        {
            value: 'customer',
            label: 'Customer Transactions',
            icon: faUser,
            subtypes: [
                { value: 'customer_payment', label: 'Customer Payment (Received)', direction: 'incoming' },
                { value: 'customer_sale', label: 'Customer Sale (Credit)', direction: 'outgoing' },
                { value: 'customer_refund', label: 'Customer Refund', direction: 'incoming' },
                { value: 'customer_to_customer', label: 'Customer to Customer Transfer', direction: 'transfer', requiresFrom: true, requiresTo: true }
            ]
        },
        {
            value: 'partner',
            label: 'Partner Transactions',
            icon: faBuilding,
            subtypes: [
                { value: 'partner_to_owner', label: 'Partner to Owner Transfer', direction: 'transfer', requiresFrom: true, convertsToUSDT: true },
                { value: 'partner_to_partner', label: 'Partner to Partner Transfer', direction: 'transfer', requiresFrom: true, requiresTo: true },
                { value: 'partner_to_supplier', label: 'Partner to Supplier Payment', direction: 'outgoing', requiresFrom: true, requiresTo: true },
                { value: 'partner_salary', label: 'Partner to Employee Salary', direction: 'outgoing', requiresFrom: true, requiresTo: true, isSpecial: true }
            ]
        },
        {
            value: 'supplier',
            label: 'Supplier Transactions',
            icon: faTruck,
            subtypes: [
                { value: 'supplier_payment', label: 'Supplier Payment', direction: 'outgoing' },
                { value: 'supplier_return', label: 'Supplier Refund', direction: 'incoming' }
            ]
        },
        {
            value: 'employee',
            label: 'Employee Transactions',
            icon: faUsers,
            subtypes: [
                { value: 'salary_payment', label: 'Salary Payment', direction: 'outgoing', isSpecial: true }
            ]
        },
        {
            value: 'other',
            label: 'Other Transactions',
            icon: faBuilding,
            subtypes: [
                { value: 'other_to_owner', label: 'Other to Owner Payment', direction: 'incoming', requiresFrom: true },
                { value: 'owner_to_other', label: 'Owner to Other Purchase', direction: 'outgoing', requiresTo: true },
                { value: 'partner_to_other', label: 'Partner to Other Payment', direction: 'outgoing', requiresFrom: true, requiresTo: true }
            ]
        },
        {
            value: 'expense',
            label: 'Expenses',
            icon: faReceipt,
            subtypes: [
                { value: 'general_expense', label: 'General Expense', direction: 'outgoing' }
            ]
        }
    ];

    const expenseCategories = [
        { id: 1, name: 'Rent', icon: faHome, color: '#FF6B6B' }, { id: 2, name: 'Utilities', icon: faBolt, color: '#4ECDC4' },
        { id: 3, name: 'Salaries', icon: faUsers, color: '#45B7D1' }, { id: 4, name: 'Inventory', icon: faBoxes, color: '#96CEB4' },
        { id: 5, name: 'Marketing', icon: faChartLine, color: '#FFEAA7' }, { id: 6, name: 'Transport', icon: faTruck, color: '#DDA0DD' },
        { id: 7, name: 'Office Supplies', icon: faPen, color: '#98D8C8' }, { id: 8, name: 'Maintenance', icon: faTools, color: '#F7B787' },
        { id: 9, name: 'Taxes', icon: faFileInvoice, color: '#E9C46A' }, { id: 10, name: 'Insurance', icon: faShieldAlt, color: '#6C5B7B' },
        { id: 11, name: 'Software', icon: faLaptopCode, color: '#355C7D' }, { id: 12, name: 'Professional', icon: faBriefcase, color: '#C06C84' },
        { id: 13, name: 'Travel', icon: faPlane, color: '#F8B195' }, { id: 14, name: 'Training', icon: faGraduationCap, color: '#B5EAD7' },
        { id: 15, name: 'Misc', icon: faEllipsisH, color: '#C7CEEA' }, { id: 16, name: 'Zakat', icon: faHandHoldingHeart, color: '#2E8B57' }
    ];

    const paymentMethodsList = [
        { value: 'cash', label: 'Cash', icon: faMoneyBillWave },
        { value: 'bank_transfer', label: 'Bank Transfer', icon: faUniversity },
        { value: 'cheque', label: 'Cheque', icon: faFileInvoice }
    ];

    const showToast = (type, message) => { setToast({ show: true, type, message }); setTimeout(() => setToast({ show: false }), 3000); };

    const currentCategory = transactionCategories.find(c => c.value === transactionCategory);
    const currentSubtype = currentCategory?.subtypes.find(s => s.value === transactionSubType);

    const needsFromSelector = currentSubtype?.requiresFrom === true;
    const needsToSelector = currentSubtype?.requiresTo === true;
    const convertsToUSDT = currentSubtype?.convertsToUSDT === true;
    const isSalaryTransaction = currentSubtype?.isSpecial === true || transactionSubType === 'salary_payment';
    const isCustomerPayment = transactionSubType === 'customer_payment';

    useEffect(() => {
        if (convertsToUSDT && amount && exchangeRate > 0) {
            setConvertedAmount(parseFloat(amount) * exchangeRate);
        } else {
            setConvertedAmount(0);
        }
    }, [amount, exchangeRate, convertsToUSDT]);

    const loadCustomerCheques = useCallback(async () => {
        if (isCustomerPayment && selectedFromCustomer && paymentMethod === 'cheque') {
            setLoadingCheques(true);
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/unified_transactions.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_customer_cheques',
                        customer_id: selectedFromCustomer.id,
                        shop_id: shopId
                    })
                });
                const data = await response.json();
                if (data.status === 'success') {
                    setCustomerCheques(data.data || []);
                }
            } catch (error) {
                console.error('Error loading cheques:', error);
            } finally {
                setLoadingCheques(false);
            }
        }
    }, [isCustomerPayment, selectedFromCustomer, paymentMethod, shopId]);

    useEffect(() => {
        loadCustomerCheques();
    }, [loadCustomerCheques]);

    const handleAddCheque = async () => {
        if (!newCheque.cheque_no || !newCheque.bank_name || !newCheque.amount) {
            showToast('error', 'Please fill in cheque number, bank name and amount');
            return;
        }
        
        setSavingCheque(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cheques.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'add',
                    cheque_no: newCheque.cheque_no,
                    bank_name: newCheque.bank_name,
                    payee_name: selectedFromCustomer?.name || newCheque.payee_name,
                    amount: parseFloat(newCheque.amount),
                    cheque_date: newCheque.cheque_date,
                    issue_date: newCheque.issue_date,
                    currency_code: newCheque.currency_code,
                    currency_symbol: newCheque.currency_symbol,
                    remarks: newCheque.remarks,
                    customer_id: selectedFromCustomer?.id,
                    customer_name: selectedFromCustomer?.name,
                    shop_id: shopId,
                    username: user?.username,
                    status: 'issued'
                })
            });
            const data = await response.json();
            
            if (data.status === 'success') {
                showToast('success', 'Cheque added successfully');
                setShowAddChequeModal(false);
                setNewCheque({
                    cheque_no: '',
                    bank_name: '',
                    payee_name: '',
                    amount: '',
                    cheque_date: new Date().toISOString().split('T')[0],
                    issue_date: new Date().toISOString().split('T')[0],
                    currency_code: 'USD',
                    currency_symbol: '$',
                    remarks: ''
                });
                loadCustomerCheques();
            } else {
                showToast('error', data.message || 'Failed to add cheque');
            }
        } catch (error) {
            console.error('Error adding cheque:', error);
            showToast('error', 'Network error occurred');
        } finally {
            setSavingCheque(false);
        }
    };

    const loadPartners = useCallback(async () => {
        if (!shopId) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/unified_transactions.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_partners', shop_id: shopId })
            });
            const data = await response.json();
            if (data.status === 'success') setPartnersList(data.data || []);
        } catch (error) { console.error('Error loading partners:', error); }
    }, [shopId]);

    const loadBalances = useCallback(async () => {
        if (!shopId) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/unified_transactions.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_balance_summary', shop_id: shopId })
            });
            const data = await response.json();
            if (data.status === 'success') setOwnerBalance(data.data.owner_balance);
        } catch (error) { console.error('Error loading balances:', error); }
    }, [shopId]);

    useEffect(() => {
        if (shopId && user?.username) {
            loadPartners();
            loadBalances();
        }
    }, [shopId, user?.username, loadPartners, loadBalances]);

    // ==============================================
    // VALIDATE FORM - FIXED: Partner only required for Bank Transfer
    // ==============================================
    const validateForm = () => {
        const newErrors = {};

        if (!transactionSubType) {
            newErrors.subtype = 'Please select a transaction type';
        }

        if (needsFromSelector && !selectedFromCustomer && !selectedFromPartner && !selectedOther) {
            newErrors.from = 'Please select a source';
        }

        if (needsToSelector && !selectedToCustomer && !selectedToPartner && !selectedEmployee && !selectedSupplier && !selectedOther) {
            newErrors.to = 'Please select a destination';
        }

        if (!isSalaryTransaction && (!amount || parseFloat(amount) <= 0)) {
            newErrors.amount = 'Amount must be greater than 0';
        }

        if (convertsToUSDT && (exchangeRate <= 0 || isNaN(exchangeRate))) {
            newErrors.rate = 'Please enter a valid exchange rate';
        }

        if (transactionCategory === 'expense' && paymentSource === 'partner' && !selectedPartner) {
            newErrors.partner = 'Please select a partner';
        }

        if (transactionSubType === 'general_expense' && !expenseCategory) {
            newErrors.category = 'Please select an expense category';
        }

        // FIX: Partner is ONLY required for Bank Transfer payments
        // For cash and cheque, partner is optional
        if (isCustomerPayment) {
            if (paymentMethod === 'cash' || paymentMethod === 'cheque') {
                // Partner is OPTIONAL for cash and cheque - no validation error
            } else if (paymentMethod === 'bank_transfer') {
                // Partner is REQUIRED only for bank transfer
                if (!selectedPartner) {
                    newErrors.partner = 'Please select a partner account (payment will be credited to partner)';
                }
            }
        }

        if (paymentMethod === 'cheque' && isCustomerPayment && !selectedCheque) {
            newErrors.cheque = 'Please select a cheque';
        }

        if ((transactionSubType === 'customer_payment' || 
             transactionSubType === 'customer_sale' || 
             transactionSubType === 'customer_refund') && 
            !selectedFromCustomer && !selectedToCustomer) {
            newErrors.entity = 'Please select a customer';
        }

        if ((transactionSubType === 'supplier_payment' || 
             transactionSubType === 'supplier_return') && 
            !selectedSupplier) {
            newErrors.entity = 'Please select a supplier';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const resetForm = () => {
        setSelectedFromCustomer(null);
        setSelectedToCustomer(null);
        setSelectedFromPartner(null);
        setSelectedToPartner(null);
        setSelectedEmployee(null);
        setSelectedOther(null);
        setSelectedSupplier(null);
        setSelectedPartner(null);
        setSelectedCheque(null);
        setAmount('');
        setReferenceNo('');
        setNotes('');
        setPaymentMethod('cash');
        setExpenseCategory(null);
        setChequeDetails({ cheque_no: '', bank_name: '', cheque_date: '', amount: '' });
        setErrors({});
    };

    const handleChequeSelect = (cheque) => {
        setSelectedCheque(cheque);
        setChequeDetails({
            cheque_no: cheque.cheque_no,
            bank_name: cheque.bank_name,
            cheque_date: cheque.cheque_date,
            amount: cheque.amount
        });
        setAmount(cheque.amount.toString());
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSalaryTransaction) {
            if (transactionSubType === 'partner_salary' && selectedFromPartner && selectedEmployee) {
                setShowSalaryModal(true);
                return;
            } else if (transactionSubType === 'salary_payment') {
                setShowSalaryModal(true);
                return;
            }
        }

        if (!validateForm()) return;

        setSaving(true);

        try {
            let payload = {
                action: 'record_transaction',
                transaction_type: transactionSubType,
                amount: convertsToUSDT ? convertedAmount : parseFloat(amount),
                original_amount: parseFloat(amount),
                original_currency: selectedCurrency,
                exchange_rate_used: exchangeRate,
                currency_code: convertsToUSDT ? 'USDT' : selectedCurrency,
                exchange_rate: exchangeRate,
                transaction_date: transactionDate,
                reference_number: referenceNo || null,
                notes: notes || null,
                payment_method: paymentMethod,
                shop_id: parseInt(shopId),
                username: user?.username,
                idempotency_key: `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            };

            if (transactionCategory === 'expense') {
                payload.payment_source = paymentSource;
                if (paymentSource === 'partner' && selectedPartner) {
                    payload.partner_id = selectedPartner.partner_id;
                    payload.partner_name = selectedPartner.partner_name;
                }
            } else {
                payload.payment_source = 'owner';
            }

            if (isCustomerPayment && selectedPartner) {
                payload.payment_source = 'partner';
                payload.partner_id = selectedPartner.partner_id;
                payload.partner_name = selectedPartner.partner_name;
            }

            if (paymentMethod === 'cheque' && selectedCheque) {
                payload.cheque_id = selectedCheque.id;
            }

            if (transactionSubType === 'customer_to_customer') {
                payload.from_customer_id = selectedFromCustomer?.id;
                payload.from_customer_name = selectedFromCustomer?.name;
                payload.to_customer_id = selectedToCustomer?.id;
                payload.to_customer_name = selectedToCustomer?.name;
                payload.entity_type = 'customer';
                payload.entity_id = selectedToCustomer?.id;
                payload.entity_name = selectedToCustomer?.name;
                payload.description = `Customer to Customer Transfer: ${selectedFromCustomer?.name} → ${selectedToCustomer?.name}`;
            }
            else if (transactionSubType === 'partner_to_partner') {
                payload.from_partner_id = selectedFromPartner?.partner_id;
                payload.from_partner_name = selectedFromPartner?.partner_name;
                payload.to_partner_id = selectedToPartner?.partner_id;
                payload.to_partner_name = selectedToPartner?.partner_name;
                payload.payment_source = 'partner';
                payload.partner_id = selectedFromPartner?.partner_id;
                payload.partner_name = selectedFromPartner?.partner_name;
                payload.entity_type = 'partner';
                payload.entity_id = selectedToPartner?.partner_id;
                payload.entity_name = selectedToPartner?.partner_name;
                payload.description = `Partner to Partner Transfer: ${selectedFromPartner?.partner_name} → ${selectedToPartner?.partner_name}`;
            }
            else if (transactionSubType === 'partner_to_owner') {
                payload.partner_id = selectedFromPartner?.partner_id;
                payload.partner_name = selectedFromPartner?.partner_name;
                payload.payment_source = 'partner';
                payload.description = `Transfer from partner ${selectedFromPartner?.partner_name}: ${amount} ${selectedCurrency} converted to ${convertedAmount} USDT using rate ${exchangeRate}`;
            }
            else if (transactionSubType === 'partner_to_supplier') {
                payload.partner_id = selectedFromPartner?.partner_id;
                payload.partner_name = selectedFromPartner?.partner_name;
                payload.entity_type = 'supplier';
                payload.entity_id = selectedSupplier?.id;
                payload.entity_name = selectedSupplier?.name;
                payload.payment_source = 'partner';
                payload.description = `Partner ${selectedFromPartner?.partner_name} paid supplier ${selectedSupplier?.name}`;
            }
            else if (transactionSubType === 'partner_salary') {
                payload.partner_id = selectedFromPartner?.partner_id;
                payload.partner_name = selectedFromPartner?.partner_name;
                payload.entity_type = 'employee';
                payload.entity_id = selectedEmployee?.employeeid;
                payload.entity_name = selectedEmployee?.employee_name;
                payload.payment_source = 'partner';
                payload.description = `Partner ${selectedFromPartner?.partner_name} paid salary to ${selectedEmployee?.employee_name}`;
            }
            else if (transactionSubType === 'other_to_owner') {
                payload.entity_type = 'other';
                payload.entity_id = selectedOther?.id;
                payload.entity_name = selectedOther?.name;
                payload.description = `Payment received from ${selectedOther?.name}`;
            }
            else if (transactionSubType === 'owner_to_other') {
                payload.entity_type = 'other';
                payload.entity_id = selectedOther?.id;
                payload.entity_name = selectedOther?.name;
                payload.description = `Purchase for ${selectedOther?.name}`;
            }
            else if (transactionSubType === 'partner_to_other') {
                payload.partner_id = selectedFromPartner?.partner_id;
                payload.partner_name = selectedFromPartner?.partner_name;
                payload.entity_type = 'other';
                payload.entity_id = selectedOther?.id;
                payload.entity_name = selectedOther?.name;
                payload.payment_source = 'partner';
                payload.description = `Partner ${selectedFromPartner?.partner_name} paid ${selectedOther?.name}`;
            }
            else if (transactionSubType === 'customer_payment') {
                payload.entity_type = 'customer';
                payload.entity_id = selectedFromCustomer?.id || selectedToCustomer?.id;
                payload.entity_name = selectedFromCustomer?.name || selectedToCustomer?.name;
                if (paymentMethod === 'cheque' && selectedCheque) {
                    payload.description = `Payment received from ${payload.entity_name} via Cheque #${selectedCheque.cheque_no} (${selectedCheque.bank_name}) credited to partner ${selectedPartner?.partner_name}`;
                } else if (paymentMethod === 'bank_transfer') {
                    payload.description = `Payment received from ${payload.entity_name} via Bank Transfer credited to partner ${selectedPartner?.partner_name}`;
                } else {
                    payload.description = `Payment received from ${payload.entity_name} credited to partner ${selectedPartner?.partner_name}`;
                }
            }
            else if (transactionSubType === 'customer_sale') {
                payload.entity_type = 'customer';
                payload.entity_id = selectedFromCustomer?.id || selectedToCustomer?.id;
                payload.entity_name = selectedFromCustomer?.name || selectedToCustomer?.name;
                payload.description = `Sale to ${payload.entity_name}`;
            }
            else if (transactionSubType === 'customer_refund') {
                payload.entity_type = 'customer';
                payload.entity_id = selectedFromCustomer?.id || selectedToCustomer?.id;
                payload.entity_name = selectedFromCustomer?.name || selectedToCustomer?.name;
                payload.description = `Refund to ${payload.entity_name}`;
            }
            else if (transactionSubType === 'supplier_payment') {
                payload.entity_type = 'supplier';
                payload.entity_id = selectedSupplier?.id;
                payload.entity_name = selectedSupplier?.name;
                payload.description = `Payment to supplier ${selectedSupplier?.name}`;
            }
            else if (transactionSubType === 'supplier_return') {
                payload.entity_type = 'supplier';
                payload.entity_id = selectedSupplier?.id;
                payload.entity_name = selectedSupplier?.name;
                payload.description = `Return from supplier ${selectedSupplier?.name}`;
            }

            if (expenseCategory) {
                payload.expense_category_id = expenseCategory.id;
                payload.expense_category_name = expenseCategory.name;
            }

            const response = await fetch('https://ourzestapp.online/oz_backend/unified_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', `Transaction recorded successfully! #${data.data.transaction_no}`);
                resetForm();
                loadBalances();
                setRefreshHistory(prev => prev + 1);
            } else {
                showToast('error', data.message || 'Failed to record transaction');
            }
        } catch (error) {
            console.error('Error:', error);
            showToast('error', 'Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleSalarySuccess = () => {
        showToast('success', 'Salary payment recorded successfully');
        resetForm();
        loadBalances();
        setRefreshHistory(prev => prev + 1);
        setShowSalaryModal(false);
    };

    // ==============================================
    // RENDER FUNCTIONS
    // ==============================================

    const renderFromSelector = () => {
        if (transactionSubType === 'customer_to_customer') {
            return (
                <div className="uni-form-group">
                    <label>From Customer <span className="uni-required">*</span></label>
                    <UTEntitySearchDropdown
                        entityType="customer"
                        onSelect={setSelectedFromCustomer}
                        selectedEntity={selectedFromCustomer}
                        shopId={shopId}
                        placeholder="Search source customer..."
                        required={true}
                    />
                    {errors.from && <div className="uni-error-message">{errors.from}</div>}
                </div>
            );
        } else if (transactionSubType === 'partner_to_partner' || transactionSubType === 'partner_to_owner' || transactionSubType === 'partner_to_supplier' || transactionSubType === 'partner_salary' || transactionSubType === 'partner_to_other') {
            return (
                <div className="uni-form-group">
                    <label>From Partner <span className="uni-required">*</span></label>
                    <select
                        value={selectedFromPartner?.partner_id || ''}
                        onChange={(e) => {
                            const partner = partnersList.find(p => p.partner_id === parseInt(e.target.value));
                            setSelectedFromPartner(partner);
                        }}
                        className="uni-form-select"
                    >
                        <option value="">Select source partner...</option>
                        {partnersList.map(p => (
                            <option key={p.partner_id} value={p.partner_id}>
                                {p.partner_name} - Balance: {formatCurrency(p.current_balance)} USDT
                            </option>
                        ))}
                    </select>
                    {errors.from && <div className="uni-error-message">{errors.from}</div>}
                </div>
            );
        } else if (transactionSubType === 'other_to_owner') {
            return (
                <div className="uni-form-group">
                    <label>From Other Account <span className="uni-required">*</span></label>
                    <UTEntitySearchDropdown
                        entityType="other"
                        onSelect={setSelectedOther}
                        selectedEntity={selectedOther}
                        shopId={shopId}
                        placeholder="Search other account..."
                        required={true}
                    />
                    {errors.from && <div className="uni-error-message">{errors.from}</div>}
                </div>
            );
        }
        return null;
    };

    const renderToSelector = () => {
        if (transactionSubType === 'customer_to_customer') {
            return (
                <div className="uni-form-group">
                    <label>To Customer <span className="uni-required">*</span></label>
                    <UTEntitySearchDropdown
                        entityType="customer"
                        onSelect={setSelectedToCustomer}
                        selectedEntity={selectedToCustomer}
                        shopId={shopId}
                        placeholder="Search destination customer..."
                        required={true}
                        excludeId={selectedFromCustomer?.id}
                    />
                    {errors.to && <div className="uni-error-message">{errors.to}</div>}
                </div>
            );
        } else if (transactionSubType === 'partner_to_partner') {
            return (
                <div className="uni-form-group">
                    <label>To Partner <span className="uni-required">*</span></label>
                    <select
                        value={selectedToPartner?.partner_id || ''}
                        onChange={(e) => {
                            const partner = partnersList.find(p => p.partner_id === parseInt(e.target.value));
                            setSelectedToPartner(partner);
                        }}
                        className="uni-form-select"
                        disabled={!selectedFromPartner}
                    >
                        <option value="">Select destination partner...</option>
                        {partnersList.filter(p => p.partner_id !== selectedFromPartner?.partner_id).map(p => (
                            <option key={p.partner_id} value={p.partner_id}>
                                {p.partner_name}
                            </option>
                        ))}
                    </select>
                    {errors.to && <div className="uni-error-message">{errors.to}</div>}
                </div>
            );
        } else if (transactionSubType === 'partner_to_supplier') {
            return (
                <div className="uni-form-group">
                    <label>To Supplier <span className="uni-required">*</span></label>
                    <UTEntitySearchDropdown
                        entityType="supplier"
                        onSelect={setSelectedSupplier}
                        selectedEntity={selectedSupplier}
                        shopId={shopId}
                        placeholder="Search supplier..."
                        required={true}
                    />
                    {errors.to && <div className="uni-error-message">{errors.to}</div>}
                </div>
            );
        } else if (transactionSubType === 'partner_salary') {
            return (
                <div className="uni-form-group">
                    <label>To Employee <span className="uni-required">*</span></label>
                    <UTEntitySearchDropdown
                        entityType="employee"
                        onSelect={setSelectedEmployee}
                        selectedEntity={selectedEmployee}
                        shopId={shopId}
                        placeholder="Search employee..."
                        required={true}
                    />
                    {errors.to && <div className="uni-error-message">{errors.to}</div>}
                </div>
            );
        } else if (transactionSubType === 'owner_to_other' || transactionSubType === 'partner_to_other') {
            return (
                <div className="uni-form-group">
                    <label>To Other Account <span className="uni-required">*</span></label>
                    <UTEntitySearchDropdown
                        entityType="other"
                        onSelect={setSelectedOther}
                        selectedEntity={selectedOther}
                        shopId={shopId}
                        placeholder="Search other account..."
                        required={true}
                    />
                    {errors.to && <div className="uni-error-message">{errors.to}</div>}
                </div>
            );
        } else if (transactionSubType === 'customer_payment' || transactionSubType === 'customer_sale' || transactionSubType === 'customer_refund') {
            return (
                <div className="uni-form-group">
                    <label>Customer <span className="uni-required">*</span></label>
                    <UTEntitySearchDropdown
                        entityType="customer"
                        onSelect={setSelectedFromCustomer}
                        selectedEntity={selectedFromCustomer}
                        shopId={shopId}
                        placeholder="Search customer..."
                        required={true}
                    />
                    {errors.entity && <div className="uni-error-message">{errors.entity}</div>}
                </div>
            );
        } else if (transactionSubType === 'supplier_payment' || transactionSubType === 'supplier_return') {
            return (
                <div className="uni-form-group">
                    <label>Supplier <span className="uni-required">*</span></label>
                    <UTEntitySearchDropdown
                        entityType="supplier"
                        onSelect={setSelectedSupplier}
                        selectedEntity={selectedSupplier}
                        shopId={shopId}
                        placeholder="Search supplier..."
                        required={true}
                    />
                    {errors.entity && <div className="uni-error-message">{errors.entity}</div>}
                </div>
            );
        }
        return null;
    };

    const AddChequeModal = () => {
        return (
            <div className="uni-modal-overlay" onClick={() => setShowAddChequeModal(false)}>
                <div className="uni-modal-content" style={{ maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
                    <div className="uni-modal-header">
                        <h3><FontAwesomeIcon icon={faPlusCircle} /> Add New Cheque for {selectedFromCustomer?.name}</h3>
                        <button className="uni-modal-close" onClick={() => setShowAddChequeModal(false)}>×</button>
                    </div>
                    <div className="uni-modal-body">
                        <div className="uni-form-group">
                            <label>Cheque Number *</label>
                            <input
                                type="text"
                                className="uni-form-input"
                                value={newCheque.cheque_no}
                                onChange={(e) => setNewCheque({ ...newCheque, cheque_no: e.target.value })}
                                placeholder="e.g., 123456"
                            />
                        </div>
                        <div className="uni-form-group">
                            <label>Bank Name *</label>
                            <input
                                type="text"
                                className="uni-form-input"
                                value={newCheque.bank_name}
                                onChange={(e) => setNewCheque({ ...newCheque, bank_name: e.target.value })}
                                placeholder="e.g., HBL, UBL, Standard Chartered"
                            />
                        </div>
                        <div className="uni-form-group">
                            <label>Amount *</label>
                            <input
                                type="number"
                                step="0.01"
                                className="uni-form-input"
                                value={newCheque.amount}
                                onChange={(e) => setNewCheque({ ...newCheque, amount: e.target.value })}
                                placeholder="0.00"
                            />
                        </div>
                        <div className="uni-details-grid">
                            <div className="uni-form-group">
                                <label>Cheque Date *</label>
                                <input
                                    type="date"
                                    className="uni-form-input"
                                    value={newCheque.cheque_date}
                                    onChange={(e) => setNewCheque({ ...newCheque, cheque_date: e.target.value })}
                                />
                            </div>
                            <div className="uni-form-group">
                                <label>Issue Date *</label>
                                <input
                                    type="date"
                                    className="uni-form-input"
                                    value={newCheque.issue_date}
                                    onChange={(e) => setNewCheque({ ...newCheque, issue_date: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="uni-form-group">
                            <label>Remarks (Optional)</label>
                            <textarea
                                className="uni-form-textarea"
                                rows="2"
                                value={newCheque.remarks}
                                onChange={(e) => setNewCheque({ ...newCheque, remarks: e.target.value })}
                                placeholder="Any additional notes..."
                            />
                        </div>
                    </div>
                    <div className="uni-modal-footer">
                        <button className="uni-btn-secondary" onClick={() => setShowAddChequeModal(false)}>Cancel</button>
                        <button className="uni-btn-primary" onClick={handleAddCheque} disabled={savingCheque}>
                            {savingCheque ? <><FontAwesomeIcon icon={faSpinner} spin /> Adding...</> : 'Add Cheque'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const SalaryPaymentModal = ({ show, onClose, onSuccess, shopId, employee, paymentSource, partnerId, partnerName }) => {
        const [amount, setAmount] = useState('');
        const [salaryMonth, setSalaryMonth] = useState(new Date().toISOString().slice(0, 7));
        const [paymentMethodLocal, setPaymentMethodLocal] = useState('cash');
        const [referenceNoLocal, setReferenceNoLocal] = useState('');
        const [notesLocal, setNotesLocal] = useState('');
        const [loading, setLoading] = useState(false);
        const { user } = useAuth();

        const handleSubmit = async (e) => {
            e.preventDefault();
            if (!amount || parseFloat(amount) <= 0) return;
            setLoading(true);
            try {
                const payload = {
                    action: 'salary_payment',
                    employee_id: employee?.employeeid || employee?.id,
                    amount: parseFloat(amount),
                    salary_month: salaryMonth + '-01',
                    payment_method: paymentMethodLocal,
                    reference_no: referenceNoLocal,
                    notes: notesLocal,
                    shop_id: shopId,
                    username: user?.username,
                    payment_source: paymentSource,
                    idempotency_key: `salary_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
                };
                if (paymentSource === 'partner' && partnerId) {
                    payload.partner_id = partnerId;
                    payload.partner_name = partnerName;
                }
                const response = await fetch('https://ourzestapp.online/oz_backend/unified_transactions.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                const data = await response.json();
                if (data.status === 'success') {
                    if (onSuccess) onSuccess(data.data);
                    onClose();
                } else {
                    alert(data.message || 'Salary payment failed');
                }
            } catch (error) {
                console.error('Salary payment error:', error);
                alert('Network error occurred');
            } finally {
                setLoading(false);
            }
        };

        if (!show) return null;

        return (
            <div className="uni-modal-overlay" onClick={onClose}>
                <div className="uni-modal-content" style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
                    <div className="uni-modal-header">
                        <h3><FontAwesomeIcon icon={faHandHoldingUsd} /> Pay Salary - {employee?.employee_name || employee?.name}</h3>
                        <button className="uni-modal-close" onClick={onClose}>×</button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="uni-modal-body">
                            <div className="uni-form-group">
                                <label>Amount *</label>
                                <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="uni-form-input" placeholder="0.00" required />
                            </div>
                            <div className="uni-form-group">
                                <label>Salary Month</label>
                                <input type="month" value={salaryMonth} onChange={(e) => setSalaryMonth(e.target.value)} className="uni-form-input" />
                            </div>
                            <div className="uni-form-group">
                                <label>Payment Method</label>
                                <select value={paymentMethodLocal} onChange={(e) => setPaymentMethodLocal(e.target.value)} className="uni-form-select">
                                    {paymentMethodsList.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                </select>
                            </div>
                            <div className="uni-form-group">
                                <label>Reference</label>
                                <input type="text" value={referenceNoLocal} onChange={(e) => setReferenceNoLocal(e.target.value)} className="uni-form-input" placeholder="Optional" />
                            </div>
                            <div className="uni-form-group">
                                <label>Notes</label>
                                <textarea value={notesLocal} onChange={(e) => setNotesLocal(e.target.value)} className="uni-form-textarea" rows="2" />
                            </div>
                        </div>
                        <div className="uni-modal-footer">
                            <button type="button" className="uni-btn-secondary" onClick={onClose}>Cancel</button>
                            <button type="submit" className="uni-btn-primary" disabled={loading}>
                                {loading ? <><FontAwesomeIcon icon={faSpinner} spin /> Processing...</> : 'Pay Salary'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    return (
        <div className={`uni-transaction-container ${isRTL ? 'uni-rtl' : ''}`}>
            <UTToastNotification toast={toast} setToast={setToast} />

            <SalaryPaymentModal
                show={showSalaryModal}
                onClose={() => setShowSalaryModal(false)}
                onSuccess={handleSalarySuccess}
                shopId={shopId}
                employee={selectedEmployee}
                paymentSource={transactionSubType === 'partner_salary' ? 'partner' : paymentSource}
                partnerId={selectedFromPartner?.partner_id}
                partnerName={selectedFromPartner?.partner_name}
            />

            {showAddChequeModal && <AddChequeModal />}

            <div className="uni-header">
                <div className="uni-tabs">
                    <button
                        className={`uni-tab ${activeTab === 'transactions' ? 'active' : ''}`}
                        onClick={() => setActiveTab('transactions')}
                    >
                        <FontAwesomeIcon icon={faExchangeAlt} /> Record Transactions
                    </button>
                   
                </div>
            </div>

            {activeTab === 'transactions' ? (
                <>
                    <div className="uni-form-container">
                        <form onSubmit={handleSubmit} className="uni-form">
                            <div className="uni-form-section">
                                <label className="uni-section-label">Transaction Category</label>
                                <div className="uni-category-grid">
                                    {transactionCategories.map(category => (
                                        <button
                                            key={category.value}
                                            type="button"
                                            className={`uni-category-btn ${transactionCategory === category.value ? 'active' : ''}`}
                                            onClick={() => {
                                                setTransactionCategory(category.value);
                                                setTransactionSubType('');
                                                resetForm();
                                            }}
                                        >
                                            <FontAwesomeIcon icon={category.icon} />
                                            <span>{category.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {transactionCategory && currentCategory?.subtypes.length > 0 && (
                                <div className="uni-form-section">
                                    <label className="uni-section-label">Transaction Type</label>
                                    <div className="uni-subtype-grid">
                                        {currentCategory.subtypes.map(subtype => (
                                            <button
                                                key={subtype.value}
                                                type="button"
                                                className={`uni-subtype-btn ${transactionSubType === subtype.value ? 'active' : ''}`}
                                                onClick={() => {
                                                    setTransactionSubType(subtype.value);
                                                    resetForm();
                                                }}
                                            >
                                                <span>{subtype.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                    {errors.subtype && <div className="uni-error-message">{errors.subtype}</div>}
                                </div>
                            )}

                            {needsFromSelector && renderFromSelector()}
                            {needsToSelector && renderToSelector()}

                            {!needsFromSelector && !needsToSelector && transactionSubType && !isSalaryTransaction && (
                                <div className="uni-form-section">
                                    {renderToSelector()}
                                </div>
                            )}

                            {!isSalaryTransaction && (
                                <div className="uni-form-section">
                                    <label className="uni-section-label">Amount</label>
                                    <div className="uni-amount-section">
                                        <div className="uni-amount-wrapper">
                                            <UTCurrencySelector
                                                selectedCurrency={selectedCurrency}
                                                onCurrencyChange={setSelectedCurrency}
                                                exchangeRate={exchangeRate}
                                                onRateChange={setExchangeRate}
                                                shopId={shopId}
                                                showRateInput={convertsToUSDT}
                                            />
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={amount}
                                                onChange={(e) => setAmount(e.target.value)}
                                                className="uni-amount-input"
                                                placeholder="0.00"
                                                disabled={paymentMethod === 'cheque' && selectedCheque}
                                            />
                                        </div>
                                        {convertsToUSDT && amount && exchangeRate > 0 && (
                                            <div className="uni-conversion-info">
                                                <FontAwesomeIcon icon={faExchangeAlt} />
                                                <span>{amount} {selectedCurrency} × {exchangeRate.toFixed(6)} = {formatCurrency(convertedAmount)} USDT (Owner will receive)</span>
                                            </div>
                                        )}
                                        {convertsToUSDT && exchangeRate <= 0 && amount > 0 && (
                                            <div className="uni-conversion-error">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                <span>Please enter an exchange rate above</span>
                                            </div>
                                        )}
                                        {errors.amount && <div className="uni-error-message">{errors.amount}</div>}
                                        {errors.rate && <div className="uni-error-message">{errors.rate}</div>}
                                    </div>
                                </div>
                            )}

                            {transactionCategory === 'expense' && transactionSubType === 'general_expense' && (
                                <div className="uni-form-section">
                                    <label className="uni-section-label">Payment Source</label>
                                    <div className="uni-source-buttons">
                                        <button type="button" className={`uni-source-btn owner ${paymentSource === 'owner' ? 'active' : ''}`} onClick={() => setPaymentSource('owner')}>
                                            <FontAwesomeIcon icon={faWallet} /> Owner Account
                                        </button>
                                        <button type="button" className={`uni-source-btn partner ${paymentSource === 'partner' ? 'active' : ''}`} onClick={() => setPaymentSource('partner')}>
                                            <FontAwesomeIcon icon={faBuilding} /> Partner Account
                                        </button>
                                    </div>
                                </div>
                            )}

                            {transactionCategory === 'expense' && paymentSource === 'partner' && (
                                <div className="uni-form-section">
                                    <label className="uni-section-label">Select Partner *</label>
                                    <select
                                        value={selectedPartner?.partner_id || ''}
                                        onChange={(e) => {
                                            const partner = partnersList.find(p => p.partner_id === parseInt(e.target.value));
                                            setSelectedPartner(partner);
                                        }}
                                        className="uni-form-select"
                                    >
                                        <option value="">Select partner...</option>
                                        {partnersList.map(p => (
                                            <option key={p.partner_id} value={p.partner_id}>
                                                {p.partner_name} - Balance: {formatCurrency(p.current_balance)} USDT
                                            </option>
                                        ))}
                                    </select>
                                    {errors.partner && <div className="uni-error-message">{errors.partner}</div>}
                                </div>
                            )}

                            {isCustomerPayment && (
                                <>
                                    <div className="uni-form-section">
                                        <label className="uni-section-label">
                                            Select Partner (Payment Receiver)
                                            {paymentMethod === 'bank_transfer' ? (
                                                <span className="uni-required-badge"> *</span>
                                            ) : (
                                                <span className="uni-optional-badge"> (Optional)</span>
                                            )}
                                        </label>
                                        <select
                                            value={selectedPartner?.partner_id || ''}
                                            onChange={(e) => {
                                                const partner = partnersList.find(p => p.partner_id === parseInt(e.target.value));
                                                setSelectedPartner(partner);
                                            }}
                                            className={`uni-form-select ${paymentMethod === 'bank_transfer' ? 'required' : 'optional'}`}
                                            required={paymentMethod === 'bank_transfer'}
                                        >
                                            <option value="">
                                                {paymentMethod === 'bank_transfer' 
                                                    ? 'Select partner account...' 
                                                    : 'Select partner account (optional)...'}
                                            </option>
                                            {partnersList.map(p => (
                                                <option key={p.partner_id} value={p.partner_id}>
                                                    {p.partner_name} - Balance: {formatCurrency(p.current_balance)} USDT
                                                </option>
                                            ))}
                                        </select>
                                        <small className={`uni-field-hint ${paymentMethod === 'bank_transfer' ? 'required-hint' : 'optional-hint'}`}>
                                            {paymentMethod === 'bank_transfer' 
                                                ? '⚠️ Partner is required for bank transfer payments. Payment will be credited to selected partner\'s bank account.'
                                                : paymentMethod === 'cheque' 
                                                ? 'ℹ️ Partner selection is optional for cheque payments. You can leave it empty.'
                                                : 'ℹ️ Partner selection is optional for cash payments. You can leave it empty.'}
                                        </small>
                                        {errors.partner && <div className="uni-error-message">{errors.partner}</div>}
                                    </div>

                                    <div className="uni-form-section">
                                        <label className="uni-section-label">Payment Method</label>
                                        <div className="uni-payment-grid">
                                            {paymentMethodsList.map(m => (
                                                <button
                                                    key={m.value}
                                                    type="button"
                                                    className={`uni-payment-btn ${paymentMethod === m.value ? 'active' : ''}`}
                                                    onClick={() => {
                                                        setPaymentMethod(m.value);
                                                        setSelectedCheque(null);
                                                        if (m.value !== 'cheque') {
                                                            setAmount('');
                                                        }
                                                        if (m.value === 'cash' || m.value === 'cheque') {
                                                            setErrors(prev => ({ ...prev, partner: '' }));
                                                        }
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={m.icon} /> {m.label}
                                                    {m.value === 'bank_transfer' ? (
                                                        <span className="payment-badge required">Required</span>
                                                    ) : (
                                                        <span className="payment-badge optional">Optional</span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {paymentMethod === 'bank_transfer' && (
                                        <div className="uni-form-section">
                                            <div className="uni-info-box warning">
                                                <FontAwesomeIcon icon={faInfoCircle} />
                                                <span>Bank transfer payment will be credited to {selectedPartner?.partner_name || 'selected partner'}'s bank account. <strong>Please select a partner above.</strong></span>
                                            </div>
                                        </div>
                                    )}

                                    {paymentMethod === 'cheque' && (
                                        <div className="uni-form-section">
                                            <div className="uni-cheque-header">
                                                <label className="uni-section-label">Select Cheque *</label>
                                                <button
                                                    type="button"
                                                    className="uni-add-cheque-btn"
                                                    onClick={() => setShowAddChequeModal(true)}
                                                >
                                                    <FontAwesomeIcon icon={faPlusCircle} /> Add New Cheque
                                                </button>
                                            </div>
                                            {loadingCheques ? (
                                                <div className="uni-loading-cheques">
                                                    <FontAwesomeIcon icon={faSpinner} spin /> Loading cheques...
                                                </div>
                                            ) : customerCheques.length > 0 ? (
                                                <div className="uni-cheque-list">
                                                    {customerCheques.map(cheque => (
                                                        <div
                                                            key={cheque.id}
                                                            className={`uni-cheque-item ${selectedCheque?.id === cheque.id ? 'selected' : ''}`}
                                                            onClick={() => handleChequeSelect(cheque)}
                                                        >
                                                            <div className="uni-cheque-info">
                                                                <div className="uni-cheque-number">
                                                                    <FontAwesomeIcon icon={faFileInvoice} />
                                                                    <strong>Cheque #: {cheque.cheque_no}</strong>
                                                                </div>
                                                                <div className="uni-cheque-bank">
                                                                    <FontAwesomeIcon icon={faUniversity} />
                                                                    {cheque.bank_name}
                                                                </div>
                                                                <div className="uni-cheque-date">
                                                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                                                    Date: {new Date(cheque.cheque_date).toLocaleDateString()}
                                                                </div>
                                                            </div>
                                                            <div className="uni-cheque-amount">
                                                                {formatCurrency(cheque.amount)} {cheque.currency_code}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="uni-no-cheques">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    <p>No active cheques found for this customer.</p>
                                                    <button 
                                                        type="button" 
                                                        className="uni-link-btn"
                                                        onClick={() => setShowAddChequeModal(true)}
                                                    >
                                                        Click here to add a cheque
                                                    </button>
                                                </div>
                                            )}
                                            {errors.cheque && <div className="uni-error-message">{errors.cheque}</div>}

                                            {selectedCheque && (
                                                <div className="uni-selected-cheque-info">
                                                    <div className="uni-info-header">
                                                        <FontAwesomeIcon icon={faCheckCircle} /> Selected Cheque Details
                                                    </div>
                                                    <div className="uni-cheque-details">
                                                        <div><strong>Cheque No:</strong> {chequeDetails.cheque_no}</div>
                                                        <div><strong>Bank:</strong> {chequeDetails.bank_name}</div>
                                                        <div><strong>Date:</strong> {new Date(chequeDetails.cheque_date).toLocaleDateString()}</div>
                                                        <div><strong>Amount:</strong> {formatCurrency(chequeDetails.amount)}</div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}

                            {transactionSubType === 'general_expense' && (
                                <div className="uni-form-section">
                                    <label className="uni-section-label">Expense Category *</label>
                                    <div className="uni-category-grid">
                                        {expenseCategories.map(cat => (
                                            <button
                                                key={cat.id}
                                                type="button"
                                                className={`uni-expense-category-btn ${expenseCategory?.id === cat.id ? 'active' : ''}`}
                                                onClick={() => setExpenseCategory(cat)}
                                                style={{
                                                    borderColor: expenseCategory?.id === cat.id ? cat.color : '#ddd',
                                                    backgroundColor: expenseCategory?.id === cat.id ? `${cat.color}20` : 'white'
                                                }}
                                            >
                                                <FontAwesomeIcon icon={cat.icon} style={{ color: cat.color }} />
                                                <span>{cat.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                    {errors.category && <div className="uni-error-message">{errors.category}</div>}
                                </div>
                            )}

                            <div className="uni-form-section">
                                <label className="uni-section-label">Transaction Details</label>
                                <div className="uni-details-grid">
                                    <div className="uni-form-group">
                                        <div className="uni-input-wrapper">
                                            <FontAwesomeIcon icon={faCalendarAlt} className="uni-input-icon" />
                                            <input type="date" value={transactionDate} onChange={(e) => setTransactionDate(e.target.value)} className="uni-form-input" />
                                            <label className="uni-floating-label">Date</label>
                                        </div>
                                    </div>
                                    <div className="uni-form-group">
                                        <div className="uni-input-wrapper">
                                            <FontAwesomeIcon icon={faBarcode} className="uni-input-icon" />
                                            <input type="text" value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} placeholder=" " className="uni-form-input" />
                                            <label className="uni-floating-label">Reference</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="uni-form-section">
                                <label className="uni-section-label">Notes</label>
                                <div className="uni-input-wrapper">
                                    <FontAwesomeIcon icon={faReceipt} className="uni-input-icon" />
                                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows="3" placeholder=" " className="uni-form-textarea" />
                                    <label className="uni-floating-label">Additional notes</label>
                                </div>
                            </div>

                            <div className="uni-form-actions">
                                <button type="button" className="uni-btn-secondary" onClick={resetForm}>
                                    <FontAwesomeIcon icon={faTimes} /> Clear
                                </button>
                                <button type="submit" className="uni-btn-primary" disabled={saving}>
                                    {saving ? <><FontAwesomeIcon icon={faSpinner} spin /> Processing...</> : <><FontAwesomeIcon icon={faSave} /> Record Transaction</>}
                                </button>
                            </div>
                        </form>
                    </div>

                    
                </>
            ) : (
                <OwnerTransactions shopId={shopId} user={user} />
            )}
        </div>
    );
};

export default UnifiedTransactionForm;